import './index.scss';
import { ID } from '@phi/newtype';
import { PropType, defineComponent, h, onMounted, ref, watch, computed } from 'vue';
import { OnshapeElement } from '@phi/onshape';
import { Unary } from '@vaaas/fpts/data';
import { Option } from '@vaaas/fpts/option';
import ListGroupSelect from '@components/ListGroupSelect';
import { OnshapeElementThumbnail } from '@features/onshape-thumbnails/components';
import { without_file_extension } from '@phi/util/file-name';
import { listDocumentElements } from '@phi/frontend/api/calls';
import { handle } from '@vaaas/fpts/result';
import { set, vif } from '@phi/util/vuetils';
import { by } from '@vaaas/fpts/iter';
import { FormText } from '@components/form';
import BackButton from '@components/back-button';
import ButtonToggle from '@components/button-toggle';
import { LoadingIndicator } from '@phi/components';

export default defineComponent({
    props: {
        document: {
            required: false,
            type: null as any as PropType<ID<'document'>>,
        },

        workspace: {
            required: false,
            type: null as any as PropType<ID<'workspace'>>,
        },

        element: {
            required: false,
            type: null as any as PropType<OnshapeElement>,
        },

        elementFilter: {
            required: false,
            type: Function as PropType<Unary<OnshapeElement, boolean>>,
            default: () => true,
        },

        back: {
            required: false,
            type: Boolean,
            default: true,
        },

        sub_heading: {
            required: false,
            type: Boolean,
            default: true,
        },

        placeholder: {
            required: false,
            type: String,
            default: 'Search by file name'
        },

        /** when enabled, add a button in the header that toggles between showing and hiding backups */
        hidden_toggle: {
            required: false,
            type: Boolean,
            default: true,
        },
    },

    emits: {
        'update:element': (x: Option<OnshapeElement>) => true,
        'back': () => true,
    },

    setup(props, ctx) {
        const SelectElement = ListGroupSelect<OnshapeElement>();

        function default_renderer(x: OnshapeElement) {
            if (!props.workspace || !props.document)
                return h('div');
            return [
                h(OnshapeElementThumbnail, {
                    value: x,
                    workspace: props.workspace,
                    document: props.document,
                }),
                h('span', { class: 'name' }, without_file_extension(x.name)),
            ];
        }

        const renderer = (ctx.slots.$element as Option<typeof default_renderer>) || default_renderer;

        const elements = ref([] as OnshapeElement[]);

        const fetching = ref(false);

        const searchText = ref('');

        async function fetch_elements() {
            if (!props.document || !props.workspace)
                return;
            fetching.value = true;
            await listDocumentElements(props.document, props.workspace, true)
                .then(handle(() => [] as OnshapeElement[]))
                .then(set(elements));
            fetching.value = false;
        }

        onMounted(fetch_elements);

        const backups = ref(false);

        watch(
            () => [props.document, props.workspace] as const,
            fetch_elements,
        );

        const transformed_elements = computed(() =>
            elements.value
                .filter(x =>
                    props.elementFilter(x)
                    && (
                        searchText.value === ''
                            ? true
                            : x.name.toLowerCase().includes(searchText.value.toLowerCase())
                    )
                    && (
                        backups.value
                        || !x.name.includes('.backup')
                    )
                )
                .sort(by(x => x.name.toLowerCase())));

        return () => h('div', { class: 'onshape-element-explorer' }, [
            h('header', { hidden: fetching.value }, [
                h('div', { class: 'first-line' }, [
                    h(BackButton, {
                        class: { hidden: !props.back },
                        onClick: () => ctx.emit('back'),
                    }),
                    h(FormText, {
                        value: searchText.value,
                        'onUpdate:value': set(searchText),
                        placeholder: props.placeholder,
                    }),
                    vif(props.hidden_toggle)(h(ButtonToggle, {
                        value: backups.value,
                        'onUpdate:value': set(backups),
                        title: 'Backups:',
                        on: 'Shown',
                        off: 'Hidden',
                    })),
                ]),
            ]),

            transformed_elements.value.length
                ? h(SelectElement, {
                    value: props.element,
                    items: transformed_elements.value.map(x => ({
                        key: x.id,
                        value: x,
                        node: renderer(x),
                    })),
                    'onUpdate:value': (x) => ctx.emit('update:element', x),
                })
                : fetching.value
                    ? ''
                    : h('div', 'No items found'),

            LoadingIndicator(!fetching.value),
        ]);
    },
});
